import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, CircleAlert, LockKeyhole } from "lucide-react";
import { Badge } from "../lib/ui";
import SubFlowNav, { FocusPanel, MetricStrip, SimpleBars } from "../components/SubFlow";
import ChainTrace from "../components/ChainTrace";
import { useAppGo } from "../lib/story";
import { navItemAllowed, showStorySpine } from "../lib/master";
import { useGuide } from "../lib/guide";
import { useSubFlow } from "../lib/subflow";
import {
  LIFE_FLOWS,
  LIFE_PHASES,
  lifecycleFocusHref,
  phaseProgress,
  scopeSteps,
} from "../lib/lifecyclePhases";

const FOCUS_TITLE = {
  overview: "Lifecycle at a glance",
  understand: "Understand — WHY and evidence",
  decide: "Decide — 7R and MDR",
  deliver: "Deliver — wave, gates and value",
  track: "Full governed track",
};

function StepCard({ step, index, current, guide, onOpen }) {
  const openable = navItemAllowed(step.route, guide);
  const Tag = openable ? "button" : "div";
  return (
    <Tag
      type={openable ? "button" : undefined}
      key={step.id}
      className={`lifecycle-step state-${step.state} ${step.id === current ? "is-current" : ""} ${openable ? "" : "is-capped"}`}
      data-testid={`lifecycle-step-${step.id}`}
      onClick={openable ? () => onOpen(step.route) : undefined}
    >
      <small>{String(index + 1).padStart(2, "0")}</small>
      <strong>{step.label}</strong>
      <Badge tone={step.state === "done" ? "green" : step.state === "current" || step.state === "ready" ? "amber" : "slate"}>
        {step.state}
      </Badge>
      <p>{step.detail}</p>
      <em>{step.purpose}</em>
      {openable ? (
        <span className="lifecycle-go">Open {step.label} <ChevronRight size={13} /></span>
      ) : (
        <span className="lifecycle-go">Outside this role</span>
      )}
    </Tag>
  );
}

function StepTrack({ steps, current, guide, onOpen, testId = "lifecycle-track" }) {
  if (!steps?.length) return null;
  return (
    <div className="lifecycle-track" data-testid={testId}>
      {steps.map((step, i) => (
        <StepCard key={step.id} step={step} index={i} current={current} guide={guide} onOpen={onOpen} />
      ))}
    </div>
  );
}

export default function Lifecycle({ app, portfolio }) {
  const goTo = useAppGo(app?.id);
  const guide = useGuide();
  const { focus } = useSubFlow("overview");
  if (!app) return null;

  const life = app.lifecycle || {};
  const steps = life.steps || [];
  const current = life.current;
  const progress = life.progress || { done: 0, total: steps.length || 22 };
  const tenant = portfolio?.tenant || { name: "Northstar Retail Group", code: "NRG-01", project: "Cloud Renewal 2025" };
  const currentStep = steps.find((s) => s.id === current) || steps[0];
  const isOverview = focus === "overview";

  const phaseBars = Object.entries(LIFE_PHASES).map(([id, phase]) => {
    const { done, total } = phaseProgress(steps, phase.stepIds);
    const pct = total ? Math.round((done / total) * 100) : 0;
    return {
      label: phase.label,
      value: pct,
      display: `${done}/${total}`,
      tone: pct === 100 ? "green" : pct > 0 ? "cyan" : "slate",
      testId: `lifecycle-phase-${id}`,
      focus: id,
    };
  });

  const scope = scopeSteps(steps);

  return (
    <div className="inner-page subflow-page lifecycle-page" data-testid="lifecycle-page">
      {!isOverview && (
        <Link to={lifecycleFocusHref(app.id, "overview")} className="analytics-back" data-testid="lifecycle-back">
          <ChevronLeft size={16} /> Back to overview
        </Link>
      )}

      <FocusPanel
        kicker={`LIFECYCLE · ${app.id}`}
        title={FOCUS_TITLE[focus] || FOCUS_TITLE.overview}
        detail={isOverview ? `${app.name} · ${progress.done}/${progress.total} steps complete` : currentStep?.purpose}
        badge={<Badge tone="cyan">{progress.done}/{progress.total}</Badge>}
        testId="lifecycle-head"
      />

      {!isOverview && <SubFlowNav items={LIFE_FLOWS} focus={focus} appId={app.id} testId="lifecycle-subflow" />}
      {showStorySpine(guide) && !isOverview && <StorySpine current="why" compact />}

      {isOverview && (
        <>
          <MetricStrip
            items={[
              { label: "Progress", value: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%`, detail: `${progress.done} of ${progress.total}`, testId: "lifecycle-progress" },
              { label: "Current step", value: currentStep?.label || "—", detail: currentStep?.state, testId: "lifecycle-current" },
              { label: "Tenant", value: tenant.code, detail: tenant.project, testId: "lifecycle-tenant" },
              { label: "WHY driver", value: app.why?.driver ? "Recorded" : "Missing", detail: app.why?.driver || "Not set", testId: "lifecycle-why" },
            ]}
          />

          <ChainTrace app={app} guide={guide} onOpen={goTo} testId="lifecycle-chain-trace" />

          <section className="feature-panel analytics-panel" data-testid="lifecycle-phase-chart">
            <div className="panel-header compact">
              <h2>Phases — click to drill down</h2>
            </div>
            <SimpleBars rows={phaseBars.map((row) => ({
              label: row.label,
              value: row.value,
              display: row.display,
              tone: row.tone,
              testId: row.testId,
            }))} testId="lifecycle-phase-bars" />
            <div className="lifecycle-phase-links">
              {Object.entries(LIFE_PHASES).map(([id, phase]) => {
                const { done, total } = phaseProgress(steps, phase.stepIds);
                return (
                  <Link key={id} to={lifecycleFocusHref(app.id, id)} className="lifecycle-phase-link" data-testid={`lifecycle-drill-${id}`}>
                    <strong>{phase.label}</strong>
                    <span>{phase.summary}</span>
                    <em>{done}/{total} done</em>
                    <ChevronRight size={16} />
                  </Link>
                );
              })}
              <Link to={lifecycleFocusHref(app.id, "track")} className="lifecycle-phase-link" data-testid="lifecycle-drill-track">
                <strong>Full track</strong>
                <span>All {steps.length} steps · executive trace</span>
                <em>{progress.done}/{progress.total}</em>
                <ChevronRight size={16} />
              </Link>
            </div>
          </section>

          {currentStep && (
            <section className="feature-panel" data-testid="lifecycle-now">
              <div className="panel-header compact">
                <h2>Now — {currentStep.label}</h2>
                <Badge tone={currentStep.state === "done" ? "green" : "amber"}>{currentStep.state}</Badge>
              </div>
              <p className="muted">{currentStep.detail}</p>
              <p className="muted small-note">{currentStep.purpose}</p>
              <div className="lifecycle-actions">
                {navItemAllowed(currentStep.route, guide) && (
                  <button type="button" className="primary-button" onClick={() => goTo(currentStep.route)}>
                    Continue <ArrowUpRight size={15} />
                  </button>
                )}
                {navItemAllowed("/assessment", guide) && (
                  <button type="button" className="secondary-button" onClick={() => goTo("/assessment")}>WHY</button>
                )}
                {navItemAllowed("/delivery", guide) && (
                  <button type="button" className="secondary-button" onClick={() => goTo("/delivery")}>Value</button>
                )}
              </div>
            </section>
          )}

          <div className="lifecycle-scope-strip" data-testid="lifecycle-context">
            {scope.map((s) => (
              <span key={s.id}><Check size={12} /> {s.label}: {s.detail}</span>
            ))}
          </div>
        </>
      )}

      {LIFE_PHASES[focus] && (
        <StepTrack
          steps={phaseProgress(steps, LIFE_PHASES[focus].stepIds).steps}
          current={current}
          guide={guide}
          onOpen={goTo}
          testId={`lifecycle-track-${focus}`}
        />
      )}

      {focus === "track" && (
        <>
          <div className="why-banner compact" data-testid="lifecycle-context-detail">
            <div>
              <span className="recommendation-label">TENANT · APPLICATION</span>
              <h2>{tenant.name} · {app.id}</h2>
              <p>{app.name} · {app.why?.driver || "WHY pending"}</p>
            </div>
          </div>
          <StepTrack steps={steps} current={current} guide={guide} onOpen={goTo} />
        </>
      )}

      {focus === "track" && app.brd?.executive_trace && (
        <div className="exec-trace" data-testid="executive-trace">
          <p className="kicker">EXECUTIVE TRACE</p>
          <div className="exec-trace-row">
            {app.brd.executive_trace.map((node, i) => {
              const openable = navItemAllowed(node.route, guide);
              const Tag = openable ? "button" : "div";
              return (
                <Tag type={openable ? "button" : undefined} key={node.id} className="exec-trace-node" onClick={openable ? () => goTo(node.route) : undefined}>
                  <small>{String(i + 1).padStart(2, "0")}</small>
                  <strong>{node.label}</strong>
                  <span>{node.value}</span>
                </Tag>
              );
            })}
          </div>
        </div>
      )}

      {!isOverview && (
        <div className="policy-note">
          <LockKeyhole size={14} />
          <span>Nothing is skipped. Nothing is invented.<br /><b>Material change creates a successor MDR.</b></span>
        </div>
      )}

      {isOverview && (
        <p className="muted small-note"><CircleAlert size={12} /> Deployment is an event. G8 proves production success and value.</p>
      )}
    </div>
  );
}
